import { useMemo, useState } from "react";
import {
  buildInviteFragment,
  computeShares,
  isValidRelayUrl,
  LIMITS,
  simplifyDebts,
  verifyOpIntegrity,
  type ExpenseBody,
  type ExpenseView,
  type GroupState,
  type Op,
} from "@psplit/ledger-core";
import type { RecurringTemplate } from "../lib/db.js";
import { createTemplate, deleteTemplate, markApplied, pendingMonths } from "../lib/recurring.js";
import { repo } from "../lib/repo.js";
import { navigate, useRecurringTemplates } from "../lib/hooks.js";
import { buildUpiLink, formatPaise, looksLikeVpa } from "../lib/money.js";
import { displayName, resolveMember } from "../lib/names.js";
import { errorMessage } from "../lib/errors.js";
import { syncGroup } from "../lib/relaySync.js";
import { QrShow } from "../components/Qr.js";
import { FaceSyncModal } from "../components/FaceSync.js";
import { NearbyHostModal } from "../components/Nearby.js";
import { MembershipCard } from "../components/Membership.js";
import { nearbyAvailable } from "../lib/nearbySession.js";
import { ExpenseForm } from "../components/ExpenseForm.js";
import { Modal } from "../components/Modal.js";

const TABS = ["expenses", "balances", "settings"] as const;

export function Group({ id, tab }: { id: string; tab: string }) {
  const meta = repo.meta(id);
  const state = repo.state(id);
  const me = repo.identity?.pubKey ?? "";
  const [joinQr, setJoinQr] = useState(false);

  if (!meta) {
    return (
      <div className="page center">
        <h2>Unknown group</h2>
        <button className="btn" onClick={() => navigate("#/")}>
          Go home
        </button>
      </div>
    );
  }

  // Old installs may still link to the retired sync/proof tabs.
  const active = (TABS as readonly string[]).includes(tab)
    ? tab
    : tab === "sync" || tab === "proof"
      ? "settings"
      : "expenses";

  const membership = repo.membership(id);
  const nameOf = (pub: string): string => (state ? displayName(state, pub) : pub.slice(0, 8));

  return (
    <div className="page">
      <header className="apphead">
        <button className="btn link" onClick={() => navigate("#/")}>
          ← All groups
        </button>
        <h1>{state?.name ?? meta.name}</h1>
        {meta.relayUrl !== "" && <HeaderSync id={id} />}
      </header>
      <nav className="tabs">
        {TABS.map((t) => (
          <button key={t} className={t === active ? "on" : ""} onClick={() => navigate(`#/g/${id}/${t}`)}>
            {t}
          </button>
        ))}
      </nav>
      {membership === "pending" && (
        <div className="notice">
          You're not in this group yet.{" "}
          {meta.relayUrl !== ""
            ? "The next time a member's app syncs, it will let you in - or do it face to face:"
            : "Show this code to any member to get in:"}
          <div className="row gap">
            <button className="btn small primary" onClick={() => setJoinQr(true)}>
              Show my join code
            </button>
          </div>
        </div>
      )}
      {!state && membership !== "pending" && <p className="muted">No entries yet.</p>}
      {active === "expenses" && state && <Expenses id={id} state={state} me={me} nameOf={nameOf} />}
      {active === "balances" && state && <Balances id={id} state={state} me={me} nameOf={nameOf} />}
      {active === "settings" && <Settings id={id} state={state} me={me} />}
      {joinQr && <FaceSyncModal id={id} autoShow onClose={() => setJoinQr(false)} />}
    </div>
  );
}

// One-tap sync from anywhere in the group. The result note sits quietly in the
// header until the next run replaces it.
function HeaderSync({ id }: { id: string }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const run = async () => {
    setBusy(true);
    setNote("");
    const result = await syncGroup(id);
    setBusy(false);
    setNote(
      result.ok
        ? result.pulled > 0
          ? `got ${result.pulled} new`
          : "up to date"
        : "couldn't reach the sync mailbox",
    );
  };

  return (
    <span className="row gap">
      {note !== "" && <span className="fineprint">{note}</span>}
      <button className="btn small" disabled={busy} onClick={() => void run()}>
        {busy ? "syncing…" : "↻ sync"}
      </button>
    </span>
  );
}

function Expenses({ id, state, me, nameOf }: { id: string; state: GroupState; me: string; nameOf: (p: string) => string }) {
  const [editing, setEditing] = useState<{ target: string; body: ExpenseBody } | null>(null);
  const [viewing, setViewing] = useState<ExpenseView | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [templates, reloadTemplates] = useRecurringTemplates(id);
  const isMember = repo.membership(id) === "member";

  const due = templates.flatMap((template) => {
    const months = pendingMonths(template, new Date());
    return months.length > 0
      ? [{ template, month: months[0] as string, more: months.length - 1 }]
      : [];
  });

  const applyDue = async (template: RecurringTemplate, month: string, record: boolean) => {
    setError("");
    try {
      if (record) await repo.addExpense(id, template.body);
      await markApplied(template.id, month);
      reloadTemplates();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <section>
      {isMember &&
        due.map(({ template, month, more }) => (
          <div key={template.id} className="notice">
            Recurring: {template.body.desc} for {month} ({formatPaise(template.body.amount)})
            {more > 0 ? `, ${more} more month${more > 1 ? "s" : ""} pending after` : ""}
            <div className="row gap">
              <button className="btn small primary" onClick={() => void applyDue(template, month, true)}>
                Add now
              </button>
              <button className="btn small" onClick={() => void applyDue(template, month, false)}>
                Skip this month
              </button>
            </div>
          </div>
        ))}
      {error !== "" && <div className="error">{error}</div>}
      {state.expenses.length === 0 && <p className="muted">No expenses yet.</p>}
      <div className="list">
        {state.expenses.map((e) => (
          <button
            key={e.id}
            className={`entry${e.voided ? " voided" : ""}`}
            onClick={() => setViewing(e)}
          >
            <div className="edesc">
              {e.desc}
              {e.edited && !e.voided && <span className="tag">edited</span>}
              {e.voided && <span className="tag">voided</span>}
            </div>
            <div className="esub">
              {nameOf(e.paidBy)} paid · split {e.split.type === "equal" ? `equally ×${e.split.participants.length}` : "by exact amounts"}
            </div>
            <div className="eamt">{formatPaise(e.amount)}</div>
          </button>
        ))}
      </div>
      {state.payments.filter((p) => !p.voided).length > 0 && (
        <>
          <h3>Payments</h3>
          <div className="list">
            {state.payments
              .filter((p) => !p.voided)
              .map((p) => (
                <div key={p.id} className="entry flat">
                  <div className="edesc">
                    {nameOf(p.from)} paid {nameOf(p.to)}
                  </div>
                  <div className="esub">{p.acked ? "receipt confirmed ✓✓" : "recorded ✓"}</div>
                  <div className="eamt">{formatPaise(p.amount)}</div>
                  {p.to === me && !p.acked && isMember && (
                    <div className="row">
                      <button className="btn small" onClick={() => void repo.ackPayment(id, p.id)}>
                        Confirm received
                      </button>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </>
      )}
      {isMember && (
        <button className="fab" onClick={() => setAdding(true)}>
          + Expense
        </button>
      )}
      {viewing && !editing && (
        <ExpenseDetail
          id={id}
          state={state}
          expense={viewing}
          me={me}
          nameOf={nameOf}
          isMember={isMember}
          onEdit={() => {
            setEditing({
              target: viewing.id,
              body: { desc: viewing.desc, amount: viewing.amount, paidBy: viewing.paidBy, split: viewing.split },
            });
            setViewing(null);
          }}
          onClose={() => setViewing(null)}
        />
      )}
      {(adding || editing) && (
        <ExpenseForm
          state={state}
          me={me}
          initial={editing?.body ?? null}
          allowRecurring={editing === null}
          onSave={async (body, repeatDay) => {
            if (editing) {
              await repo.supersedeEntry(id, editing.target, body);
            } else {
              await repo.addExpense(id, body);
              if (repeatDay !== null) {
                await createTemplate(id, body, repeatDay);
                reloadTemplates();
              }
            }
          }}
          onVoid={
            editing
              ? async () => {
                  await repo.supersedeEntry(id, editing.target, null);
                }
              : null
          }
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

// Tap-through view of one expense: who paid, everyone's share, and - when you
// owe the payer for it - settle THIS expense directly (UPI deep link and/or a
// signed payment op for exactly your share).
function ExpenseDetail({
  id,
  state,
  expense: e,
  me,
  nameOf,
  isMember,
  onEdit,
  onClose,
}: {
  id: string;
  state: GroupState;
  expense: ExpenseView;
  me: string;
  nameOf: (p: string) => string;
  isMember: boolean;
  onEdit: () => void;
  onClose: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const shares = useMemo(() => computeShares(e.amount, e.split), [e]);
  const resolve = (pub: string) => state.claims.get(pub) ?? pub;

  const payer = resolve(e.paidBy);
  // My share includes any split entries still recorded under a ghost I claimed.
  let myShare = 0;
  for (const [p, share] of shares) if (resolve(p) === me) myShare += share;

  const canSettle = isMember && !e.voided && payer !== me && myShare > 0;
  const payerInfo = resolveMember(state, e.paidBy);
  const upiOk = canSettle && payerInfo && looksLikeVpa(payerInfo.vpa);

  const recordShare = async () => {
    setError("");
    setBusy(true);
    try {
      await repo.addPayment(id, { from: me, to: payer, amount: myShare, note: e.desc });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h3>
        {e.desc}
        {e.edited && !e.voided && <span className="tag">edited</span>}
        {e.voided && <span className="tag">voided</span>}
      </h3>
      <div className="esub">
        {nameOf(e.paidBy)}
        {payer === me ? " (you)" : ""} paid {formatPaise(e.amount)} · {new Date(e.ts).toLocaleDateString()}
      </div>
      <div className="list">
        {[...shares].map(([p, share]) => (
          <div key={p} className="entry flat">
            <div className="edesc">
              {nameOf(p)}
              {resolve(p) === me ? " (you)" : ""}
            </div>
            <div className="eamt">{formatPaise(share)}</div>
          </div>
        ))}
      </div>
      {canSettle && (
        <p className="hint">
          Your share is {formatPaise(myShare)}, owed to {nameOf(e.paidBy)}.
        </p>
      )}
      {canSettle && !upiOk && payerInfo && !payerInfo.isGhost && (
        <p className="fineprint">
          {nameOf(e.paidBy)} has not added a UPI id yet - ask them to set one in their
          profile for one-tap pay.
        </p>
      )}
      {error !== "" && <div className="error">{error}</div>}
      <div className="row gap wrap">
        {upiOk && (
          <a
            className="btn small"
            href={buildUpiLink({
              vpa: (payerInfo as { vpa: string }).vpa,
              name: nameOf(e.paidBy),
              paise: myShare,
              note: e.desc,
            })}
          >
            Pay via UPI
          </a>
        )}
        {canSettle && (
          <button className="btn small" disabled={busy} onClick={() => void recordShare()}>
            Record my share as paid
          </button>
        )}
        {e.author === me && !e.voided && isMember && (
          <button className="btn small" onClick={onEdit}>
            Edit
          </button>
        )}
        <button className="btn small" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

function Balances({ id, state, me, nameOf }: { id: string; state: GroupState; me: string; nameOf: (p: string) => string }) {
  const [error, setError] = useState("");
  const transfers = useMemo(() => simplifyDebts(state.balances), [state]);
  const isMember = repo.membership(id) === "member";

  const record = async (from: string, to: string, amount: number) => {
    setError("");
    try {
      await repo.addPayment(id, { from, to, amount, note: "settling up" });
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <section>
      <div className="list">
        {state.members
          .filter((m) => m.active || (state.balances.get(m.pubKey) ?? 0) !== 0)
          .map((m) => {
            const b = state.balances.get(m.pubKey) ?? 0;
            return (
              <div key={m.pubKey} className="entry flat">
                <div className="edesc">
                  {nameOf(m.pubKey)}
                  {m.pubKey === me ? " (you)" : ""}
                  {!m.active && <span className="tag">left</span>}
                </div>
                <div className={b > 0 ? "eamt ok" : b < 0 ? "eamt due" : "eamt"}>
                  {b === 0 ? "settled" : b > 0 ? `gets ${formatPaise(b)}` : `owes ${formatPaise(-b)}`}
                </div>
              </div>
            );
          })}
      </div>
      {transfers.length > 0 && (
        <>
          <h3>Simplest way to settle</h3>
          <div className="list">
            {transfers.map((t, i) => {
              const creditor = state.members.find((m) => m.pubKey === t.to);
              const upiOk = t.from === me && creditor && looksLikeVpa(creditor.vpa);
              return (
                <div key={i} className="entry flat">
                  <div className="edesc">
                    {nameOf(t.from)} pays {nameOf(t.to)}
                  </div>
                  <div className="eamt">{formatPaise(t.amount)}</div>
                  <div className="row gap">
                    {upiOk && (
                      <a
                        className="btn small"
                        href={buildUpiLink({
                          vpa: (creditor as { vpa: string }).vpa,
                          name: nameOf(t.to),
                          paise: t.amount,
                          note: state.name,
                        })}
                      >
                        Pay via UPI
                      </a>
                    )}
                    {isMember && (
                      <button className="btn small" onClick={() => void record(t.from, t.to, t.amount)}>
                        Record as paid
                      </button>
                    )}
                  </div>
                  {t.from === me && !upiOk && creditor && !creditor.isGhost && (
                    <p className="fineprint">
                      {nameOf(t.to)} has not added a UPI id yet - ask them to set one in
                      their profile for one-tap pay.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      {error !== "" && <div className="error">{error}</div>}
    </section>
  );
}

function Proof({ id, nameOf }: { id: string; nameOf: (p: string) => string }) {
  const store = repo.storeOf(id);
  const ops = useMemo(() => store.topoOrder().reverse(), [store, store.size]);
  const checks = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const op of ops) map.set(op.hash, verifyOpIntegrity(op).ok);
    return map;
  }, [ops]);
  const allOk = [...checks.values()].every(Boolean);

  return (
    <section>
      <div className={allOk ? "notice ok" : "error"}>
        {allOk
          ? `Ledger verified: ${ops.length} entries, every signature valid, hash chain intact. Nobody, including us, can silently rewrite this history.`
          : "TAMPERING DETECTED: some entries fail verification."}
      </div>
      <div className="list mono">
        {ops.map((op: Op) => (
          <div key={op.hash} className="entry flat">
            <div className="edesc">
              <span className="tag">{op.kind}</span> by {nameOf(op.author)}
            </div>
            <div className="esub">
              #{op.lamport} · {op.hash.slice(0, 12)}… ← [{op.parents.map((p) => p.slice(0, 6)).join(", ") || "genesis"}]
            </div>
            <div className={checks.get(op.hash) ? "eamt ok" : "eamt due"}>
              {checks.get(op.hash) ? "✓ signed" : "✗ INVALID"}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Settings({ id, state, me }: { id: string; state: GroupState | null; me: string }) {
  const meta = repo.meta(id);
  const [profileName, setProfileName] = useState(repo.profileName);
  const [vpa, setVpa] = useState(repo.profileVpa);
  const [relayUrl, setRelayUrl] = useState(meta?.relayUrl ?? "");
  const [status, setStatus] = useState("");
  const [busySync, setBusySync] = useState(false);
  const [faceSync, setFaceSync] = useState(false);
  const [showProof, setShowProof] = useState(false);
  const [showIdentity, setShowIdentity] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [inviteQr, setInviteQr] = useState<string[] | null>(null);
  const [nearby, setNearby] = useState(false);
  const [ghostName, setGhostName] = useState("");
  const [settingsTemplates, reloadSettingsTemplates] = useRecurringTemplates(id);
  const isMember = repo.membership(id) === "member";

  if (!meta) return null;

  const inviteUrl = `${window.location.origin}/#${buildInviteFragment({
    groupId: id,
    epoch: repo.epoch(id),
    key: repo.groupKey(id),
    relayUrl: meta.relayUrl,
    name: state?.name ?? meta.name,
  })}`;

  const copyInvite = async () => {
    await navigator.clipboard.writeText(inviteUrl);
    setStatus("Invite link copied. It's the key to this group - share it only with people you want in.");
  };

  const saveProfile = async () => {
    if (vpa !== "" && !looksLikeVpa(vpa)) {
      setStatus("That UPI id does not look valid (name@bank).");
      return;
    }
    await repo.setProfile(profileName.trim(), vpa.trim());
    setStatus("Saved. Your groups will see the update.");
  };

  const doSync = async () => {
    setBusySync(true);
    setStatus("");
    const result = await syncGroup(id);
    setBusySync(false);
    setStatus(
      result.ok
        ? `Done. Sent ${result.pushed}, received ${result.pulled}.${result.skipped > 0 ? ` ${result.skipped} couldn't be read.` : ""}`
        : "Couldn't reach the sync mailbox. Check your connection and try again.",
    );
  };

  const saveRelay = async () => {
    if (relayUrl !== "" && !isValidRelayUrl(relayUrl)) {
      setStatus("The address should start with https://");
      return;
    }
    await repo.updateMeta(id, { relayUrl: relayUrl.trim() });
    setStatus("Saved for this group on this phone. Others get theirs from a fresh invite link.");
  };

  const exportLedger = () => {
    const text = repo.exportBundleText(id);
    const blob = new Blob([text], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `paisasplit-${(state?.name ?? meta.name).replace(/\W+/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const importLedger = async (file: File) => {
    try {
      const result = await repo.importBundleText(id, await file.text());
      setStatus(
        result.added > 0
          ? `Read ${result.added} entr${result.added === 1 ? "y" : "ies"} from the file.${result.rejected > 0 ? ` ${result.rejected} couldn't be read.` : ""}`
          : "Nothing new in that file - you already had all of it.",
      );
    } catch (e) {
      setStatus(`Couldn't read that file: ${errorMessage(e)}`);
    }
  };

  const sendSyncFile = async () => {
    try {
      const text = await repo.exportSyncText(id);
      const fname = `paisasplit-sync-${(state?.name ?? meta.name).replace(/\W+/g, "-")}.txt`;
      const file = new File([text], fname, { type: "text/plain" });
      if (typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "PaisaSplit sync file" });
        setStatus("Sent. On their phone: open this group, Settings, then \"Read a sync file\".");
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(file);
        a.download = fname;
        a.click();
        URL.revokeObjectURL(a.href);
        setStatus("Saved as a file. Send it however you like - it only opens inside this group.");
      }
    } catch (e) {
      if ((e as { name?: string }).name !== "AbortError") setStatus(errorMessage(e));
    }
  };

  return (
    <section>
      <div className="card">
        <h3>Staying in sync</h3>
        {meta.relayUrl !== "" ? (
          <>
            <p className="fineprint">
              New entries travel between phones through a mailbox on the internet. Everything is
              locked with the group's key before it leaves your phone, so the mailbox can never
              read your expenses.
            </p>
            <div className="row gap wrap">
              <button className="btn primary" disabled={busySync} onClick={() => void doSync()}>
                {busySync ? "Syncing…" : "Sync now"}
              </button>
            </div>
            <label className="check">
              <input
                type="checkbox"
                checked={meta.autoSync !== false}
                onChange={(e) => void repo.updateMeta(id, { autoSync: e.target.checked })}
              />
              Sync by itself after I add or change something
            </label>
          </>
        ) : (
          <p className="fineprint">
            This group has no internet mailbox, so it only syncs face to face (below). To sync
            over the internet, add a mailbox address under Advanced.
          </p>
        )}
        <details className="adv">
          <summary>Advanced: mailbox address</summary>
          <label>
            Mailbox address for this group
            <input value={relayUrl} onChange={(e) => setRelayUrl(e.target.value)} placeholder="https://…" />
          </label>
          <button className="btn" onClick={() => void saveRelay()}>
            Save address
          </button>
          <p className="fineprint">
            Everyone got this address with their invite. Change it only if the whole group is
            moving to a new mailbox.
          </p>
        </details>

        <h4>In person</h4>
        <p className="fineprint">
          No internet needed. One phone shows a code, the other scans it, and new entries hop
          across. Or send everything as a locked file over WhatsApp, Quick Share or AirDrop.
        </p>
        <div className="row gap wrap">
          <button className="btn primary" onClick={() => setFaceSync(true)}>
            Show / scan codes
          </button>
          {isMember && (
            <button className="btn" onClick={() => void sendSyncFile()}>
              Send as file
            </button>
          )}
          <label className="btn">
            Read a sync file
            <input
              type="file"
              accept=".txt,.json,text/plain,application/json"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void importLedger(file);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      </div>

      <div className="card">
        <h3>Invite people</h3>
        <p className="fineprint">
          This link is the key to your group: anyone holding it can read the ledger and join.
          Share it like a house key - only with people you trust.
        </p>
        <div className="row gap wrap">
          <button className="btn primary" onClick={() => void copyInvite()}>
            Copy invite link
          </button>
          <button className="btn" onClick={() => setInviteQr([inviteUrl])}>
            Show invite QR
          </button>
          {nearbyAvailable() && meta.relayUrl !== "" && repo.membership(id) === "member" && (
            <button className="btn" onClick={() => setNearby(true)}>
              Add people nearby
            </button>
          )}
        </div>
        {nearbyAvailable() && meta.relayUrl !== "" && (
          <p className="fineprint">
            Adding several people at one table is quicker by sound: they all hear one chirp, and
            you wave each of them in. Unlike the link, nothing anyone overhears lets them read the
            group.
          </p>
        )}
      </div>

      <div className="card">
        <h3>Your profile</h3>
        <label>
          Name
          <input value={profileName} onChange={(e) => setProfileName(e.target.value)} maxLength={LIMITS.name} />
        </label>
        <label>
          UPI id (so friends can pay you in one tap)
          <input value={vpa} onChange={(e) => setVpa(e.target.value)} maxLength={LIMITS.vpa} placeholder="you@bank" />
        </label>
        <button className="btn" onClick={() => void saveProfile()}>
          Save profile
        </button>
      </div>

      <div className="card">
        <h3>People</h3>
        <div className="list">
          {(state?.members ?? []).map((m) => (
            <div key={m.pubKey} className="entry flat">
              <div className="edesc">
                {m.name !== "" ? m.name : m.pubKey.slice(0, 8)}
                {m.pubKey === me ? " (you)" : ""}
                {m.isGhost && <span className="tag">no app yet</span>}
                {!m.active && <span className="tag">left</span>}
              </div>
              {m.isGhost && isMember && (
                <div className="row">
                  <button
                    className="btn small"
                    onClick={() => {
                      void repo
                        .claimGhost(id, m.pubKey)
                        .then(() => setStatus(`You took over ${m.name}'s history.`))
                        .catch((e: unknown) => setStatus(errorMessage(e)));
                    }}
                  >
                    That's me
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <label>
          Add someone without the app
          <input value={ghostName} onChange={(e) => setGhostName(e.target.value)} maxLength={LIMITS.name} placeholder="Their name" />
        </label>
        <button
          className="btn"
          disabled={!isMember || ghostName.trim() === ""}
          onClick={() => {
            void repo
              .addGhost(id, ghostName.trim())
              .then(() => {
                setGhostName("");
                setStatus("Added. They can owe, be owed, and pay like anyone else.");
              })
              .catch((e: unknown) => setStatus(errorMessage(e)));
          }}
        >
          Add person
        </button>
        <p className="fineprint">
          For friends who won't install anything: they can still owe, be owed and pay. When they
          finally do install, they tap "That's me" on their own phone and their history and
          balance come with them.
        </p>
      </div>

      <div className="card">
        <details className="adv">
          <summary>Recurring expenses</summary>
        {settingsTemplates.length === 0 ? (
          <p className="muted">None. Tick "Repeat monthly" when adding an expense.</p>
        ) : (
          <div className="list">
            {settingsTemplates.map((t) => (
              <div key={t.id} className="entry flat">
                <div className="edesc">{t.body.desc}</div>
                <div className="esub">
                  every month on day {t.day} · {formatPaise(t.body.amount)}
                </div>
                <div className="row">
                  <button
                    className="btn small danger"
                    onClick={() => {
                      void deleteTemplate(t.id).then(reloadSettingsTemplates);
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="fineprint">
          These reminders live only on this phone. Nothing is ever added to the ledger without
          you tapping Add.
        </p>
        </details>
      </div>

      <div className="card">
        <details className="adv">
          <summary>Backup: save or restore this group</summary>
          <p className="fineprint">
            Export writes this group's full history into a file you keep - handy for moving to a
            new phone. The file is plain and readable, so store it somewhere private. Import
            reads one back in.
          </p>
          <div className="row gap wrap">
            <button className="btn" onClick={exportLedger}>
              Export history file
            </button>
            <label className="btn">
              Import history file
              <input
                type="file"
                accept=".json,.txt,application/json,text/plain"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void importLedger(file);
                  e.target.value = "";
                }}
              />
            </label>
            <button className="btn" onClick={() => setShowIdentity((v) => !v)}>
              {showIdentity ? "Hide my signature key" : "Show my signature key"}
            </button>
          </div>
          {showIdentity && (
            <>
              <p className="error">
                This is your personal signature - PaisaSplit uses it to prove your entries came
                from you. Anyone who has it can write entries in your name, so keep it private
                and safe.
              </p>
              <textarea readOnly rows={5} value={repo.exportIdentity()} onFocus={(e) => e.target.select()} />
            </>
          )}
        </details>
      </div>

      {state && <MembershipCard id={id} state={state} me={me} />}

      {state && (
        <div className="card">
          <details className="adv">
            <summary>Check the books</summary>
            <p className="fineprint">
              Every entry is signed by whoever wrote it and stitched to the entry before it, like
              pages sewn into a bahi khata. If anyone - even us - changed an old entry, the
              stitching would break. You can check it any time.
            </p>
            <button className="btn" onClick={() => setShowProof(true)}>
              Check all entries
            </button>
          </details>
        </div>
      )}

      <div className="card">
        <details className="adv">
          <summary>Leave or remove this group</summary>
          <h4>Leave group</h4>
        <p className="fineprint">
          Leaving is written into the ledger and your past entries stay, so everyone's totals
          still add up. You can be added back any time. Anyone holding an old invite link can
          still read the group until somebody changes the key.
        </p>
        {!confirmLeave ? (
          <button className="btn danger" disabled={repo.membership(id) !== "member"} onClick={() => setConfirmLeave(true)}>
            Leave group
          </button>
        ) : (
          <div className="row gap">
            <button
              className="btn danger"
              onClick={() => {
                void repo.leaveGroup(id).then(() => setConfirmLeave(false));
              }}
            >
              Confirm leave
            </button>
            <button className="btn" onClick={() => setConfirmLeave(false)}>
              Cancel
            </button>
          </div>
        )}

        <h4>Remove from this phone</h4>
        <p className="fineprint">
          Wipes this group from this phone only. Everyone else keeps their copy, and an invite
          link (or a saved history file) brings it back.
        </p>
        {!confirmRemove ? (
          <button className="btn danger" onClick={() => setConfirmRemove(true)}>
            Remove group from device
          </button>
        ) : (
          <div className="row gap">
            <button
              className="btn danger"
              onClick={() => {
                void repo.removeGroup(id).then(() => navigate("#/"));
              }}
            >
              Confirm remove
            </button>
            <button className="btn" onClick={() => setConfirmRemove(false)}>
              Cancel
            </button>
          </div>
        )}
        </details>
      </div>

      {status !== "" && <div className="notice">{status}</div>}
      {inviteQr && (
        <Modal onClose={() => setInviteQr(null)}>
          <QrShow frames={inviteQr} caption="Scan with any camera app to open the invite." />
          <button className="btn" onClick={() => setInviteQr(null)}>
            Done
          </button>
        </Modal>
      )}
      {faceSync && <FaceSyncModal id={id} onClose={() => setFaceSync(false)} />}
      {nearby && <NearbyHostModal id={id} onClose={() => setNearby(false)} />}
      {showProof && state && (
        <Modal onClose={() => setShowProof(false)}>
          <h3>Checking the books</h3>
          <Proof id={id} nameOf={(p) => displayName(state, p)} />
          <button className="btn" onClick={() => setShowProof(false)}>
            Close
          </button>
        </Modal>
      )}
      <div className="fineprint">you: {me.slice(0, 16)}…</div>
    </section>
  );
}
